// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import { getColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { validateDNI } from "utils/validations";
import { validarName } from "utils/validations";

export default function ModalInfraccion({ open, handleClose, idInfraccion, refetch }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [usuarios, setUsuarios] = useState([]);
  const { confirmar } = useContext(ConfirmarContext);
  const [step, setStep] = useState(0);
  const [thisInfraccion, setThisInfraccion] = useState(null);

  const [getInfraccion, { data: infraccion, loading }] = useLazyQuery(gql`
    query getInfraccion($id: ID!) {
      infraccion(id: $id) {
        id
        categoria
        fecha
        dni
        nombre
        apellido
        usuario {
          id
        }
        localidad {
          id
        }
        nroExpediente
        nroResolucion
        motivo
        monto
        sancion
        tipoInfraccion
      }
    }
  `);

  const [getUsuarios, { loading: loadingUsuarios }] = useLazyQuery(gql`
    query getUsuarios($filter: JSON) {
      usuarios(filter: $filter) {
        id
        nombre
        apellido
        dni
        username
      }
    }
  `);

  const { data: localidades } = useQuery(gql`
    query GetLocalidades {
      localidades(provincia: 20) {
        id
        nombre
      }
    }
  `);

  const [saveInfraccion, { loading: loadingSave }] = useMutation(gql`
    mutation saveInfraccion($input: InfraccionInput!) {
      saveInfraccion(input: $input) {
        id
      }
    }
  `);

  const [deleteInfraccion, { loading: loadingDelete }] = useMutation(
    gql`
      mutation deleteInfraccion($id: ID!) {
        deleteInfraccion(id: $id) {
          id
        }
      }
    `
  );

  useEffect(() => {
    if (idInfraccion && open) {
      getInfraccion({ variables: { id: idInfraccion } }).then((res) => {
        setThisInfraccion({
          id: res?.data?.infraccion?.id,
          idUser: res?.data?.infraccion?.usuario?.id,
          dni: res?.data?.infraccion?.dni,
          nombre: res?.data?.infraccion?.nombre,
          apellido: res?.data?.infraccion?.apellido,
          fecha: res?.data?.infraccion?.fecha,
          categoria: res?.data?.infraccion?.categoria,
          nroExpediente: res?.data?.infraccion?.nroExpediente,
          nroResolucion: res?.data?.infraccion?.nroResolucion,
          idLocalidad: res?.data?.infraccion?.localidad?.id,
          tipoInfraccion: res?.data?.infraccion?.tipoInfraccion,
          monto: res?.data?.infraccion?.monto,
          motivo: res?.data?.infraccion?.motivo,
          sancion: res?.data?.infraccion?.sancion,
        });
        setStep(2);
      });
    } else {
      setThisInfraccion({
        fecha: dayjs().format("YYYY-MM-DD"),
      });
      setStep(0);
      setUsuarios([]);
    }
  }, [idInfraccion, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90%",
            width: { xs: "90%", sm: step === 2 ? "70%" : "40%", xxl: step === 2 ? "60%" : "30%" },
            overflowY: "auto",
            transition: "all 0.5s",
            p: 3,
          }}
        >
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {infraccion?.infraccion?.id ? `Editar infracción` : "Nueva infracción"}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox>
              {step === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="fechaPermiso">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Indique el DNI del infractor
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="dni"
                      name="dni"
                      value={thisInfraccion?.dni}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, dni: e.target.value })
                      }
                      error={thisInfraccion?.dni?.length > 0 && !validateDNI(thisInfraccion?.dni)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SoftButton
                      color="primary"
                      disabled={
                        loadingUsuarios ||
                        !thisInfraccion?.dni ||
                        (thisInfraccion?.dni?.length > 0 && !validateDNI(thisInfraccion?.dni))
                      }
                      onClick={() => {
                        getUsuarios({ variables: { filter: { dni: thisInfraccion?.dni } } }).then(
                          (res) => {
                            if (res?.data?.usuarios?.length === 1) {
                              setThisInfraccion({
                                dni: res?.data?.usuarios[0].dni,
                                idUser: res?.data?.usuarios[0].id,
                                fecha: dayjs().format("YYYY-MM-DD"),
                                nombre: res?.data?.usuarios[0].nombre,
                                apellido: res?.data?.usuarios[0].apellido,
                              });
                              handleSnackbar("Se ha encontrado un usuario con ese DNI", "info");
                              setStep(2);
                            } else if (res?.data?.usuarios?.length === 0) {
                              setStep(2);
                              handleSnackbar("No se ha encontrado un usuario con ese DNI", "info");
                            } else {
                              setUsuarios(res?.data?.usuarios);
                              setStep(1);
                              handleSnackbar(
                                "Se han encontrado múltiples usuarios con ese DNI",
                                "info"
                              );
                            }
                          }
                        );
                      }}
                      fullWidth
                    >
                      Buscar&nbsp;
                      <Icon>search</Icon>
                    </SoftButton>
                  </Grid>
                </Grid>
              )}
              {step === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SoftTypography variant="h6">
                      Se han encontrado múltiples usuarios con ese DNI
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        px={2}
                        py={1}
                        sx={{
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                        onClick={(e) => {
                          setExpanded(
                            expanded.includes("Historial de Permisos")
                              ? expanded.filter((i) => i !== "Historial de Permisos")
                              : [...expanded, "Historial de Permisos"]
                          );
                        }}
                      >
                        <SoftBox display="flex" alignItems="center">
                          <SoftBox mr={1} display="flex" alignItems="center">
                            <Icon
                              sx={{
                                color: getColor("gobSecondary"),
                              }}
                            >
                              group
                            </Icon>
                          </SoftBox>
                          <SoftTypography variant="h6" fontWeight="bold">
                            Seleccione un usuario para continuar
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      <Collapse in={usuarios.length}>
                        <SoftBox
                          sx={{
                            maxHeight: "50vh",
                            overflowY: "auto",
                          }}
                        >
                          {usuarios?.map((usuario, index) => (
                            <SoftBox key={usuario.id}>
                              <SoftBox
                                px={2}
                                py={1}
                                sx={{
                                  borderBottom:
                                    index === usuarios.length - 1
                                      ? "none"
                                      : "1px solid rgba(0, 0, 0, 0.12)",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                <SoftBox display="flex" justifyContent="space-between">
                                  <SoftBox
                                    display="flex"
                                    alignItems="center"
                                    pl={{
                                      xs: 0,
                                      sm: 2,
                                    }}
                                  >
                                    <SoftBox
                                      mr={1}
                                      sx={{
                                        display: {
                                          xs: "none",
                                          sm: "block",
                                        },
                                      }}
                                    >
                                      <Icon
                                        sx={{
                                          color: getColor("gobPrimary"),
                                        }}
                                      >
                                        subdirectory_arrow_right
                                      </Icon>
                                    </SoftBox>
                                    <SoftBox
                                      mr={1}
                                      sx={{
                                        display: {
                                          xs: "none",
                                          sm: "flex",
                                        },
                                      }}
                                    >
                                      <Tooltip title={"Usuario"} placement="top">
                                        <Icon
                                          sx={{
                                            color: getColor("gobSecondary"),
                                          }}
                                        >
                                          person
                                        </Icon>
                                      </Tooltip>
                                    </SoftBox>
                                    <SoftBox display="flex" flexDirection="column">
                                      <SoftTypography variant="h6">
                                        {`${usuario.apellido} ${usuario.nombre}`}
                                      </SoftTypography>
                                      <SoftTypography variant="caption" fontWeight="regular">
                                        {`DNI: ${formatMoneyPunto(usuario.dni)} - ${
                                          usuario.username
                                        }`}
                                      </SoftTypography>
                                    </SoftBox>
                                  </SoftBox>
                                  <SoftBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                  >
                                    <Tooltip title={"Seleccionar"} placement="top">
                                      <SoftButton
                                        color={"gobSecondary"}
                                        iconOnly
                                        onClick={() => {
                                          setThisInfraccion({
                                            dni: usuario.dni,
                                            idUser: usuario.id,
                                            fecha: dayjs().format("YYYY-MM-DD"),
                                          });
                                          setStep(2);
                                        }}
                                      >
                                        <Icon>arrow_forward</Icon>
                                      </SoftButton>
                                    </Tooltip>
                                  </SoftBox>
                                </SoftBox>
                              </SoftBox>
                            </SoftBox>
                          ))}
                        </SoftBox>
                      </Collapse>
                    </Card>
                  </Grid>
                </Grid>
              )}
              {step === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="dni">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        DNI del infractor
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="dni"
                      name="dni"
                      value={thisInfraccion?.dni}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, dni: e.target.value })
                      }
                      disabled={thisInfraccion?.idUser}
                      error={thisInfraccion?.dni?.length > 0 && !validateDNI(thisInfraccion?.dni)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="nombre">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Nombre del infractor
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="nombre"
                      name="nombre"
                      value={thisInfraccion?.nombre}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, nombre: e.target.value })
                      }
                      disabled={thisInfraccion?.idUser}
                      error={
                        thisInfraccion?.nombre?.length > 0 && !validarName(thisInfraccion?.nombre)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="apellido">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Apellido del infractor
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="apellido"
                      name="apellido"
                      value={thisInfraccion?.apellido}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, apellido: e.target.value })
                      }
                      disabled={thisInfraccion?.idUser}
                      error={
                        thisInfraccion?.apellido?.length > 0 &&
                        !validarName(thisInfraccion?.apellido)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="fecha">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Fecha de la infracción
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="fecha"
                      name="fecha"
                      type="date"
                      value={thisInfraccion?.fecha}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, fecha: e.target.value })
                      }
                      inputProps={{
                        max: dayjs().format("YYYY-MM-DD"),
                      }}
                      error={thisInfraccion?.fecha && dayjs(thisInfraccion?.fecha).isAfter(dayjs())}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="categoria">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Categoría
                      </SoftTypography>
                    </InputLabel>
                    <Select
                      value={thisInfraccion?.categoria || -1}
                      onChange={(e) => {
                        setThisInfraccion({ ...thisInfraccion, categoria: e.target.value });
                      }}
                    >
                      <MenuItem value={-1} disabled>
                        Seleccione una categoría
                      </MenuItem>
                      <MenuItem value={"Bosque"}>Bosque</MenuItem>
                      <MenuItem value={"Fauna"}>Fauna</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="nroExpediente">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Número de expediente
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="nroExpediente"
                      name="nroExpediente"
                      value={thisInfraccion?.nroExpediente}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, nroExpediente: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="nroResolucion">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Número de resolución
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="nroResolucion"
                      name="nroResolucion"
                      value={thisInfraccion?.nroResolucion}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, nroResolucion: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="localidad">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Localidad donde ocurrió
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <Select
                      name="localidad"
                      value={thisInfraccion?.idLocalidad || -1}
                      onChange={(e) =>
                        setThisInfraccion({
                          ...thisInfraccion,
                          idLocalidad: e.target.value,
                          localidad: localidades?.localidades.find(
                            (localidad) => localidad.id === e.target.value
                          ),
                        })
                      }
                    >
                      <MenuItem value={-1} disabled>
                        Seleccione una
                      </MenuItem>
                      {localidades?.localidades?.map((localidad) => (
                        <MenuItem key={localidad.id} value={localidad.id}>
                          {localidad.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <InputLabel htmlFor="tipoInfraccion">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Tipo de infracción
                      </SoftTypography>
                    </InputLabel>
                    <Select
                      value={thisInfraccion?.tipoInfraccion || []}
                      onChange={(e) => {
                        setThisInfraccion({ ...thisInfraccion, tipoInfraccion: e.target.value });
                      }}
                      multiple
                    >
                      <MenuItem value={-1} disabled>
                        Seleccione uno o más tipos
                      </MenuItem>
                      <MenuItem value={"Pesca ilegal"}>Pesca ilegal</MenuItem>
                      <MenuItem value={"Caza ilegal"}>Caza ilegal</MenuItem>
                      <MenuItem value={"Residuos peligrosos"}>Residuos peligrosos</MenuItem>
                      <MenuItem value={"Agroquimicos"}>Agroquimicos</MenuItem>
                      <MenuItem value={"Impacto ambiental"}>Impacto ambiental</MenuItem>
                      <MenuItem value={"Residuos Solidos Urbanos"}>
                        Residuos Solidos Urbanos
                      </MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputLabel htmlFor="monto">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Monto de la multa
                      </SoftTypography>
                    </InputLabel>
                    <SoftInputNumber
                      id="monto"
                      name="monto"
                      value={thisInfraccion?.monto}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, monto: e.target.value })
                      }
                      icon={{
                        component: <Icon>attach_money</Icon>,
                        direction: "left",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="motivo">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Motivo
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="motivo"
                      name="motivo"
                      value={thisInfraccion?.motivo}
                      multiline
                      rows={4}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, motivo: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="sancion">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Sanción
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      id="sancion"
                      name="sancion"
                      value={thisInfraccion?.sancion}
                      multiline
                      rows={4}
                      onChange={(e) =>
                        setThisInfraccion({ ...thisInfraccion, sancion: e.target.value })
                      }
                    />
                  </Grid>
                </Grid>
              )}
            </SoftBox>
            <Collapse in={step > 0}>
              <SoftBox
                mt={2}
                display="flex"
                justifyContent={thisInfraccion?.id ? "flex-end" : "space-between"}
                gap={2}
              >
                {thisInfraccion?.id && (
                  <SoftButton
                    color="error"
                    disabled={loadingDelete || loadingSave}
                    onClick={() => {
                      deleteInfraccion({
                        variables: {
                          id: thisInfraccion?.id,
                        },
                      })
                        .then(() => {
                          refetch();
                          handleClose();
                          handleSnackbar("Infracción eliminada correctamente", "success");
                        })
                        .catch((e) => {
                          handleSnackbar(
                            e.message || "Ha ocurrido un error al eliminar la infracción",
                            "error"
                          );
                        });
                    }}
                  >
                    <Icon>delete</Icon>
                    &nbsp;Eliminar
                  </SoftButton>
                )}
                <SoftButton
                  color="primary"
                  disabled={loadingDelete || loadingSave}
                  onClick={() => {
                    if (!thisInfraccion?.id) {
                      setStep(0);
                    } else {
                      handleClose();
                    }
                  }}
                >
                  <Icon>{thisInfraccion?.id ? "cancel" : "arrow_back"}</Icon>
                  &nbsp;{thisInfraccion?.id ? "Cancelar" : "Atrás"}
                </SoftButton>
                {step !== 1 && (
                  <SoftButton
                    color="gobSecondary"
                    disabled={
                      (step === 2 &&
                        (!thisInfraccion?.dni ||
                          (thisInfraccion?.dni?.length > 0 && !validateDNI(thisInfraccion?.dni)) ||
                          !thisInfraccion?.fecha ||
                          !thisInfraccion?.categoria ||
                          !thisInfraccion?.nroExpediente ||
                          !thisInfraccion?.nroResolucion ||
                          !thisInfraccion?.idLocalidad ||
                          !thisInfraccion?.tipoInfraccion ||
                          !thisInfraccion?.monto ||
                          !thisInfraccion?.motivo ||
                          !thisInfraccion?.sancion)) ||
                      loadingSave ||
                      loadingDelete
                    }
                    onClick={() => {
                      if (thisInfraccion?.id) {
                        let dataToSave = { ...thisInfraccion };
                        delete dataToSave.localidad;
                        saveInfraccion({
                          variables: {
                            input: {
                              ...dataToSave,
                              id: infraccion?.infraccion?.id,
                            },
                          },
                        })
                          .then(() => {
                            refetch();
                            handleClose();
                            handleSnackbar("Infracción guardada correctamente", "success");
                          })
                          .catch((e) => {
                            handleSnackbar(
                              e.message || "Ha ocurrido un error al guardar la infracción",
                              "error"
                            );
                          });
                      } else if (step === 2) {
                        confirmar({
                          title: "Generar infracción",
                          message: (
                            <SoftTypography variant="h6" fontWeight="light">
                              ¿Está seguro que desea generar una infracción por{" "}
                              <span style={{ fontWeight: "bold" }}>{`$${formatMoneyPunto(
                                thisInfraccion?.monto
                              )}`}</span>{" "}
                              para{" "}
                              <span style={{ fontWeight: "bold" }}>{`${thisInfraccion?.apellido} ${
                                thisInfraccion?.nombre
                              } con DNI: ${formatMoneyPunto(thisInfraccion?.dni)}`}</span>
                              ?
                            </SoftTypography>
                          ),
                          icon: "warning",
                          func: () => {
                            let dataToSave = { ...thisInfraccion };
                            delete dataToSave.localidad;
                            saveInfraccion({
                              variables: {
                                input: {
                                  ...dataToSave,
                                },
                              },
                            })
                              .then(() => {
                                refetch();
                                handleClose();
                                handleSnackbar("Infracción guardada correctamente", "success");
                              })
                              .catch((e) => {
                                handleSnackbar(
                                  e.message || "Ha ocurrido un error al guardar la infracción",
                                  "error"
                                );
                              });
                          },
                        });
                      }
                    }}
                  >
                    {thisInfraccion?.id ? "Guardar" : "Siguiente"}&nbsp;
                    <Icon>{thisInfraccion?.id ? "save" : "arrow_forward"}</Icon>
                  </SoftButton>
                )}
              </SoftBox>
            </Collapse>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalInfraccion.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idInfraccion: PropTypes.string,
  refetch: PropTypes.func,
};
